import { makeAutoObservable, observable } from "mobx";
import api, {
  GetCampaignListParameters, PutCampaignParameters,
  PostCampaignParameters, DeleteCampaignParameters, GetCampaignRes, GetCampaignListRes, PostCampaignRes
} from "../api";
import {
  createRequestStatus,
  handleLoadMoreRequest,
  handleRequest
} from "../toll/toolRequestStatus";



class CampaignStore {
  // 所有公司列表
  campaignList: GetCampaignRes[] = [];
  total = 0;
  lastId?: string
  noMoreData?: boolean

  // 公司详情缓存：key 为 campaignId
  campaignMap = observable.map<string, GetCampaignRes>();

  // 请求状态
  campaignListStatus = createRequestStatus();
  campaignStatusMap = observable.map<string, ReturnType<typeof createRequestStatus>>();
  createCampaignStatus = createRequestStatus();
  createCampaigSuccess = false
  createCampaigData?: PostCampaignRes = undefined

  updateCampaignStatus = createRequestStatus();
  deleteCampaignStatus = createRequestStatus();

  loadMoreStatus = createRequestStatus(); // "加载更多"的loading

  constructor () {
    makeAutoObservable(this);
  }

  /** 获取第一页公司（或用于刷新） */
  getCampaignList = async (params?: Partial<GetCampaignListParameters>) => {
    this.resetCampaignList()
    await handleRequest<GetCampaignListRes>(
      this.campaignListStatus,
      () => api.GetCampaignList({ ...params }),
      (res) => {
        this.campaignList = res?.items || [];
        this.total = Math.max(this.total, res?.total || 0);
        // 讀取最後一項，不可用 pop 刪除畫面正在使用的資料。
        this.lastId = res.items?.[res.items.length - 1]?.id
      },
      "获取公司列表失败"
    );
  };


  // 使用通用 handleLoadMoreRequest
  loadMoreCampaign = async (params?: Partial<GetCampaignListParameters>) => {
    if (!this.hasMore || this.loadMoreStatus.loading) return;
    await handleLoadMoreRequest<GetCampaignRes, GetCampaignListRes>({
      dataList: this.campaignList,
      setDataList: arr => { this.campaignList = arr; },
      limit: params?.limit,
      lastId: this.lastId,
      // 游標接口回傳的是剩餘總數，保留首批查詢的完整總數。
      setTotal: () => {},
      statusObj: this.loadMoreStatus,
      setNoMoreData: flag => { this.noMoreData = flag; },
      apiCall: (limit, lastId) => api.GetCampaignList({ limit, lastId, ...params || {} }),
      onSuccess: (res) => {
        this.total = Math.max(this.total, res?.total || 0);
        // 讀取最後一項，不可用 pop 刪除畫面正在使用的資料。
        this.lastId = res.items?.[res.items.length - 1]?.id
      }, // 可选
      defaultErrMsg: "加载更多失败"
    });
  };



  /** 是否还有下一页 */
  get hasMore() {
    return !this.noMoreData && this.campaignList.length < this.total;
  }

  /** 可选：重置数据 */
  resetCampaignList() {
    this.campaignList = [];
    this.total = 0;
    this.lastId = undefined
    this.noMoreData = undefined
  }

  /** 获取公司详情 */
  getCampaign = async (campaignId: string) => {
    if (!this.campaignStatusMap.has(campaignId)) {
      this.campaignStatusMap.set(campaignId, createRequestStatus());
    }
    const statusObj = this.campaignStatusMap.get(campaignId)!;

    await handleRequest<GetCampaignRes>(
      statusObj,
      () => api.GetCampaign({ id: campaignId }),
      (res) => {
        this.campaignMap.set(campaignId, res);
      },
      "获取公司详情失败"
    );
  };


  resetCreateCampaign() {
    this.createCampaigSuccess = false
    this.createCampaigData = undefined
  }

  /** 新增公司 */
  createCampaign = async (params: PostCampaignParameters) => {
    await handleRequest<PostCampaignRes>(
      this.createCampaignStatus,
      () => api.PostCampaign(params),
      (res) => {
        this.createCampaigData = res
        this.createCampaigSuccess = true
      },
      "新增公司失败"
    );
  };

  /** 更新公司 */
  updateCampaign = async (params: PutCampaignParameters) => {
    await handleRequest(
      this.updateCampaignStatus,
      () => api.PutCampaign(params),
      (res) => {
        // 更新后建议刷新对应详情及列表
        if (params.id) {
          this.getCampaign(params.id);
        }
        this.getCampaignList({});
      },
      "更新公司失败"
    );
  };

  /** 删除公司 */
  deleteCampaign = async (params: DeleteCampaignParameters) => {
    await handleRequest(
      this.deleteCampaignStatus,
      () => api.DeleteCampaign(params),
      (res) => {
        // 删除后自动刷新列表，公司详情可选移除
        this.getCampaignList({});
        if (params.id) {
          this.campaignMap.delete(params.id);
          this.campaignStatusMap.delete(params.id);
        }
      },
      "删除公司失败"
    );
  };


}

const campaignStore = new CampaignStore();
export default campaignStore;
